/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { DefaultProps } from '../MdIvesCmpuRegPage';
import { findMdPrjInfo, saveMdPrjInfo } from 'apis/md/MdProject';
import { MdIvesPrjInfo } from 'models/md/MdIves';
import CustomTextarea from 'components/inputs/CustomTextarea';
import { useNavigate } from 'react-router-dom';
import Dropzone from 'components/dropzone/Dropzone';
import { FileSaveResponse, FileSaveResult } from 'models/admin/FileInfo';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { NormalButton } from 'components/buttons/ButtonStyle';
import EltrCellLineInqPopup from '../../popup/EltrCellLineInqPopup';
import CustomerRegistPopup from '../../popup/CustomerRegistPopup';

const PrjInfoPage = ({ elmPrjId }: DefaultProps) => {
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const initPrjInfo = {
    elmPrjId: '',
    elmPrjNo: '',
    elmPrjTpCd: '',
    cellFertTpCd: '',
    elmPrjNm: '',
    elmPrjDesc: '',
    copCd: '',
    bildPlntCds: [],
    bildPlntCd: '',
    bildPlntCdNm: '',
    prjDivsCd: '',
    prjId: '',
    prjNm: '',
    ivesStDt: '',
    ivesEndDt: '',
    elmPrjStatCd: '',
    elmPrjStatCdNm: '',
    eltrLnNm: '',
    asyLnNm: '',
    mduLnNm: '',
    custNm: '',
    wbsCd: '',
    ivesAmt: '',
    bmkYn: '',
    rmk: '',
    atchFileGrId: '',
    useYn: '',
    optValCtn1: '',
    optValCtn2: '',
    optValCtn3: '',
    optValCtn4: '',
    optValCtn5: '',
    dataInsUserId: '',
    dataInsUserNm: '',
    dataInsDtm: '',
    dataUpdUserId: '',
    dataUpdUserNm: '',
    dataUpdDtm: '',
    mdlNm: '',
    mktgMgr: '',
    ivesMgr: '',
    mktgManagers: [],
    ivesManagers: [],
  };

  const dropzoneRef = useRef<any>();
  const [errors, setErrors] = useState<any>();
  const [prjInfo, setPrjInfo] = useState<MdIvesPrjInfo>(initPrjInfo);
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [popups, setPopups] = useState({
    eltrLn: false,
    asyLn: false,
    customer: false,
    mktgMgr: false,
    ivesMgr: false,
  });

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    setBildPlntCode(bildPlntCode);
  };

  const handleOnChange = (name: string, value: any) => {
    setPrjInfo((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const btnList = () => {
    navigate('/md/ives-cmpu');
  };

  const btnReset = () => {
    setPrjInfo(initPrjInfo);
  };

  const isValid = () => {
    setErrors((prev) => ({
      ...prev,
      elmPrjNm: !prjInfo.elmPrjNm,
      bildPlntCd: !prjInfo.bildPlntCd,
      eltrLnNm: !prjInfo.eltrLnNm,
      asyLnNm: !prjInfo.asyLnNm,
      custNm: !prjInfo.custNm,
      ivesMgr: !prjInfo.ivesManagers?.length,
      mktgMgr: !prjInfo.mktgManagers?.length,
      elmPrjDesc: !prjInfo.elmPrjDesc,
    }));

    return (
      !!prjInfo.elmPrjNm &&
      !!prjInfo.bildPlntCd &&
      !!prjInfo.eltrLnNm &&
      !!prjInfo.asyLnNm &&
      !!prjInfo.custNm &&
      !!prjInfo.ivesManagers?.length &&
      !!prjInfo.mktgManagers?.length &&
      !!prjInfo.elmPrjDesc
    );
  };

  const btnSave = async () => {
    if (!isValid()) {
      return;
    }

    openLoading(true);
    const fileSaveResponse: FileSaveResponse = await dropzoneRef.current.saveFiles();
    if (fileSaveResponse.fileSaveResult == FileSaveResult.FAIL) return;
    const atchFileGrId = fileSaveResponse.atchFileGrId;

    saveMdPrjInfo({ ...prjInfo, atchFileGrId })
      .then((res) => {
        if (res.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
          });
        } else {
          openMessageBar({
            type: 'error',
            content: t('com.msg.저장에 실패했습니다.', '저장에 실패했습니다.'),
          });
        }
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnAddCustomer = () => {
    setPopups((prev) => ({ ...prev, customer: true }));
  };

  const openLinePopup = (gbn: string) => {
    if (!prjInfo.bildPlntCd) {
      setErrors((prev) => ({ ...prev, bildPlntCd: true }));
      return;
    }

    setPopups((prev) => ({ ...prev, [gbn]: true }));
  };

  useEffect(() => {
    initCodes();
  }, []);

  useEffect(() => {
    if (!elmPrjId) {
      btnReset();
    } else {
      openLoading(true);
      findMdPrjInfo(elmPrjId)
        .then((res) => {
          if (res.data)
            setPrjInfo({
              ...res.data,
              ivesMgr: res.data.ivesManagers?.map((o) => o.wrtUserNm).join(),
              mktgMgr: res.data.mktgManagers?.map((o) => o.wrtUserNm).join(),
            });
        })
        .catch((err) => {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        })
        .finally(() => openLoading(false));
    }
  }, [elmPrjId]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.프로젝트 정보', '프로젝트 정보')}</h3>
        </SubTitleGroup>
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnList}>
            {t('com.button.목록', '목록')}
          </Button>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnReset}>
            {t('com.button.초기화', '초기화')}
          </Button>
          <Button css={IconButton.button} className="save" disabled={isLoading} onClick={btnSave}>
            {t('com.label.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      </SubTitleLayout>
      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.프로젝트명', '프로젝트명'))}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={prjInfo?.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  isError={errors?.elmPrjNm}
                  msgError={String(
                    t('setup.msg.프로젝트명을 입력해 주세요.', '프로젝트명을 입력해 주세요.')
                  )}
                ></CustomInputWithSearch>
              </TableCell>
              <TableCell>{String(t('mp.label.작성자', '작성자'))}</TableCell>
              <TableCell>{prjInfo?.dataInsUserNm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.플랜트', '플랜트'))}</span>
              </TableCell>
              <TableCell>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={prjInfo?.bildPlntCds}
                  onChange={(value) => handleOnChange('bildPlntCds', value)}
                  isError={errors?.bildPlntCd}
                  msgError={String(
                    t('setup.msg.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.')
                  )}
                />
              </TableCell>
              <TableCell>{String(t('mp.label.작성일자', '작성일자'))}</TableCell>
              <TableCell>{prjInfo?.dataInsDtm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.전극 라인', '전극 라인'))}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="eltrLnNm"
                  className="find"
                  value={prjInfo?.eltrLnNm}
                  onChange={(value) => handleOnChange('eltrLnNm', value)}
                  onSearchClick={(e) => openLinePopup('eltrLn')}
                  isError={errors?.eltrLnNm}
                  msgError={String(
                    t('setup.msg.전극 라인을 입력해 주세요.', '전극 라인을 입력해 주세요.')
                  )}
                ></CustomInputWithSearch>
              </TableCell>
              <TableCell>{String(t('mp.label.수정자', '수정자'))}</TableCell>
              <TableCell>{prjInfo?.dataUpdUserNm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.셀 라인', '셀 라인'))}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="asyLnNm"
                  className="find"
                  value={prjInfo?.asyLnNm}
                  onChange={(value) => handleOnChange('asyLnNm', value)}
                  onSearchClick={(e) => openLinePopup('asyLn')}
                  isError={errors?.asyLnNm}
                  msgError={String(
                    t('setup.msg.셀 라인을 입력해 주세요.', '셀 라인을 입력해 주세요.')
                  )}
                ></CustomInputWithSearch>
              </TableCell>
              <TableCell>{String(t('mp.label.수정일자', '수정일자'))}</TableCell>
              <TableCell>{prjInfo?.dataUpdDtm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.고객사', '고객사'))}</span>
              </TableCell>
              <TableCell>
                <div className="flex">
                  <ComboBox
                    classNameWrap="grow"
                    options={bildPlntCode}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={prjInfo?.custNm}
                    onChange={(value) => handleOnChange('custNm', value)}
                    isError={errors?.custNm}
                    msgError={String(
                      t('setup.msg.고객사를 선택해 주세요.', '고객사를 선택해 주세요.')
                    )}
                  />
                  <NormalButton
                    css={IconButton.button}
                    className="icoNone ml-2"
                    size="sm"
                    onClick={btnAddCustomer}
                  >
                    {t('com.button.추가', '추가')}
                  </NormalButton>
                </div>
              </TableCell>
              <TableCell>{String(t('mp.label.상태', '상태'))}</TableCell>
              <TableCell>{prjInfo?.elmPrjStatCdNm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.검토 담당자', '검토 담당자'))}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="ivesMgr"
                  className="find"
                  value={prjInfo.ivesMgr}
                  isError={errors?.ivesMgr}
                  onSearchClick={(e) => setPopups((prev) => ({ ...prev, ivesMgr: true }))}
                  msgError={String(
                    t('setup.msg.검토 담당자를 선택해 주세요.', '검토 담당자를 선택해 주세요.')
                  )}
                ></CustomInputWithSearch>
              </TableCell>
              <TableCell rowSpan={2}>{String(t('mp.label.첨부파일', '첨부파일'))}</TableCell>
              <TableCell rowSpan={2}>
                <Dropzone
                  ref={dropzoneRef}
                  atchFileGrIdInput={prjInfo?.atchFileGrId}
                  atchFileTpCd="MD_PRJ_INFO"
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">
                  {String(t('mp.label.마케팅팀 담당자', '마케팅팀 담당자'))}
                </span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  name="elmPrjId"
                  className="find"
                  value={prjInfo.mktgMgr}
                  isError={errors?.mktgMgr}
                  onSearchClick={(e) => setPopups((prev) => ({ ...prev, mktgMgr: true }))}
                  msgError={String(
                    t(
                      'setup.msg.마케팅팀 담당자를 선택해 주세요.',
                      '마케팅팀 담당자를 선택해 주세요.'
                    )
                  )}
                ></CustomInputWithSearch>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.프로젝트 상세', '프로젝트 상세'))}</span>
              </TableCell>
              <TableCell colSpan={3}>
                <CustomTextarea
                  name={'elmPrjDesc'}
                  value={prjInfo?.elmPrjDesc}
                  isError={errors?.elmPrjDesc}
                  style={{
                    height: '250px',
                  }}
                  onChange={(e) => handleOnChange('elmPrjDesc', e.target.value)}
                  msgError={String(
                    t('setup.msg.프로젝트 상세를 입력해 주세요.', '프로젝트 상세를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {popups.asyLn && prjInfo.bildPlntCd && (
        <EltrCellLineInqPopup
          open={popups.asyLn}
          close={() => setPopups((prev) => ({ ...prev, asyLn: false }))}
          condition={{
            bildPlntCd: prjInfo.bildPlntCd,
            bildPlntCdNm: bildPlntCode.find((o) => o.cmnCd === prjInfo.bildPlntCd)?.cmnCdNm,
            prdnProcCd: '',
          }}
        />
      )}
      {popups.eltrLn && prjInfo.bildPlntCd && (
        <EltrCellLineInqPopup
          open={popups.eltrLn}
          close={() => setPopups((prev) => ({ ...prev, eltrLn: false }))}
          condition={{
            bildPlntCd: prjInfo.bildPlntCd,
            bildPlntCdNm: bildPlntCode.find((o) => o.cmnCd === prjInfo.bildPlntCd)?.cmnCdNm,
            prdnProcCd: '',
          }}
        />
      )}
      {popups.customer && (
        <CustomerRegistPopup
          open={popups.customer}
          close={() => setPopups((prev) => ({ ...prev, customer: false }))}
        />
      )}
      {popups.ivesMgr && (
        <UserMultiModal
          open={popups.ivesMgr}
          close={() => setPopups((prev) => ({ ...prev, ivesMgr: false }))}
          title={t('com.label.검토 담당자 조회', '검토 담당자 조회')}
          condition={{
            roles: ['FA_USER'],
          }}
          defaultUserId={''}
          singleSelect={false}
          onCallback={(callbackData: any) => {
            if (callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return false;
            }

            setPrjInfo((prev) => ({
              ...prev,
              ivesManagers: callbackData.userId.split(',').map((o: string) => ({ wrtUserId: o })),
              ivesMgr: callbackData.empNm,
            }));
          }}
        />
      )}
      {popups.mktgMgr && (
        <UserMultiModal
          open={popups.mktgMgr}
          close={() => setPopups((prev) => ({ ...prev, mktgMgr: false }))}
          title={t('com.label.마케팅팀 담당자 조회', '마케팅팀 담당자 조회')}
          condition={{
            roles: ['FA_USER'],
          }}
          defaultUserId={''}
          singleSelect={false}
          onCallback={(callbackData: any) => {
            if (callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return false;
            }

            setPrjInfo((prev) => ({
              ...prev,
              mktgManagers: callbackData.userId.split(',').map((o: string) => ({ wrtUserId: o })),
              mktgMgr: callbackData.empNm,
            }));
          }}
        />
      )}
    </>
  );
};

export default PrjInfoPage;
